import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import Avatar from "./Avatar";
import { toast } from "sonner";
import { Send, Hash, Users, MessageCircle, Trash2, Search, ChevronLeft, Plus, UserPlus, LogOut as LogOutIcon, Pencil, Paperclip, FileText, Image as ImageIcon, Download, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";

const POLL_MS = 5000;

function MessageBubble({ msg, mine, onDelete, canDeleteAny }) {
  const ts = new Date(msg.created_at);
  const att = msg.attachment;
  return (
    <div className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${msg.id}`}>
      {!mine && (
        <Avatar avatar={msg.sender_avatar} name={msg.sender_name?.split(" ")[0]} lastname={msg.sender_name?.split(" ").slice(1).join(" ")} size={32} />
      )}
      <div className={`max-w-[75%] group ${mine ? "items-end" : "items-start"} flex flex-col`}>
        {!mine && (
          <div className="text-[11px] text-[#757575] mb-0.5 px-2 flex items-center gap-1">
            <span className="font-semibold">{msg.sender_name}</span>
            {msg.sender_role === "admin" && <span className="bg-[#3B82F6]/10 text-[#7C3AED] text-[9px] px-1.5 py-0 rounded-full uppercase tracking-widest font-bold">Admin</span>}
            {msg.sender_role === "sub_admin" && <span className="bg-blue-100 text-blue-700 text-[9px] px-1.5 py-0 rounded-full uppercase tracking-widest font-bold">Sub-Admin</span>}
          </div>
        )}
        <div className={`relative rounded-2xl text-sm whitespace-pre-wrap break-words overflow-hidden ${mine ? "bg-[#3B82F6] text-white rounded-br-sm" : "bg-white border border-[#1F2540] text-[#0A0A0A] rounded-bl-sm"} ${att ? "p-1.5" : "px-3.5 py-2"}`}>
          {att && att.kind === "image" ? (
            <a href={att.data_url} download={att.filename} target="_blank" rel="noreferrer" data-testid={`chat-att-img-${msg.id}`}>
              <img src={att.data_url} alt={att.filename} className="block max-w-full max-h-64 rounded-xl object-cover"/>
              <div className={`flex items-center gap-1 px-2 py-1 text-[10px] ${mine ? "text-white/80" : "text-[#757575]"}`}>
                <ImageIcon className="w-3 h-3"/> <span className="truncate flex-1">{att.filename}</span>
                <Download className="w-3 h-3"/>
              </div>
            </a>
          ) : att && att.kind === "pdf" ? (
            <a
              href={att.data_url}
              download={att.filename}
              target="_blank"
              rel="noreferrer"
              data-testid={`chat-att-pdf-${msg.id}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl ${mine ? "bg-white/10 hover:bg-white/20" : "bg-[#1E2347] hover:bg-[#FFE0C3]"} transition`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${mine ? "bg-white/20" : "bg-[#3B82F6]"}`}>
                <FileText className={`w-5 h-5 ${mine ? "text-white" : "text-white"}`}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold truncate ${mine ? "text-white" : "text-[#0A0A0A]"}`}>{att.filename}</div>
                <div className={`text-[10px] ${mine ? "text-white/70" : "text-[#757575]"}`}>{Math.round(att.size / 1024)} KB · PDF</div>
              </div>
              <Download className={`w-4 h-4 flex-shrink-0 ${mine ? "text-white" : "text-[#3B82F6]"}`}/>
            </a>
          ) : (
            <div className="px-3.5 py-2 -m-0">{msg.body}</div>
          )}
          {(mine || canDeleteAny) && (
            <button
              onClick={() => onDelete(msg.id)}
              data-testid={`chat-msg-delete-${msg.id}`}
              className={`opacity-0 group-hover:opacity-100 absolute -top-2 -right-2 rounded-full p-1 transition shadow ${mine ? "bg-white text-red-600" : "bg-red-600 text-white"}`}
              title="Elimina messaggio"
            >
              <Trash2 className="w-3 h-3"/>
            </button>
          )}
        </div>
        <div className="text-[10px] text-[#999] mt-0.5 px-2">{ts.toLocaleTimeString("it-IT", {hour:"2-digit", minute:"2-digit"})}</div>
      </div>
    </div>
  );
}

export default function ChatModule({ embedded = false }) {
  const { user } = useAuth();
  const canDeleteAny = ["admin", "sub_admin", "superadmin"].includes(user?.role);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeChannel, setActiveChannel] = useState("general");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [unreadMap, setUnreadMap] = useState({});
  const [showSidebar, setShowSidebar] = useState(true);
  // Group create dialog
  const [groupDialog, setGroupDialog] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", member_ids: [] });
  // Group manage dialog
  const [manageGroup, setManageGroup] = useState(null); // group object being managed
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const prevMessagesLenRef = useRef(0);

  const loadContacts = useCallback(async () => {
    try {
      const [c, g, u] = await Promise.all([
        api.get("/chat/contacts"),
        api.get("/chat/groups"),
        api.get("/chat/unread"),
      ]);
      setContacts(c.data);
      setGroups(g.data || []);
      setUnreadMap(u.data.channels || {});
    } catch (_) { /* noop */ }
  }, []);

  const loadMessages = useCallback(async (channelId) => {
    try {
      const r = await api.get(`/chat/messages?channel_id=${encodeURIComponent(channelId)}`);
      setMessages(r.data);
      // mark as read
      api.post("/chat/mark-read", { channel_id: channelId }).catch(() => {});
      setUnreadMap((m) => { const cp = { ...m }; delete cp[channelId]; return cp; });
    } catch (_) { /* noop */ }
  }, []);

  // Initial load + polling
  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => { loadMessages(activeChannel); }, [activeChannel, loadMessages]);
  useEffect(() => {
    const t = setInterval(() => {
      loadMessages(activeChannel);
      loadContacts();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [activeChannel, loadMessages, loadContacts]);

  // Auto-scroll intelligente:
  // - Se l'utente è in fondo, scrolla automaticamente al nuovo messaggio.
  // - Se sta leggendo messaggi più in alto, NON scrolla e incrementa il contatore "nuovi messaggi".
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prevLen = prevMessagesLenRef.current;
    const newLen = messages.length;
    prevMessagesLenRef.current = newLen;
    if (newLen === 0) {
      setNewMessagesCount(0);
      return;
    }
    if (atBottom || newLen < prevLen) {
      // Scroll to bottom (anche su cambio canale, dove newLen < prevLen può succedere)
      el.scrollTop = el.scrollHeight;
      setNewMessagesCount(0);
    } else if (newLen > prevLen) {
      setNewMessagesCount((c) => c + (newLen - prevLen));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Reset contatore quando si cambia canale
  useEffect(() => {
    setNewMessagesCount(0);
    setAtBottom(true);
  }, [activeChannel]);

  const onScrollMessages = (e) => {
    const el = e.currentTarget;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isBottom = dist < 40;
    setAtBottom(isBottom);
    if (isBottom) setNewMessagesCount(0);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setNewMessagesCount(0);
    setAtBottom(true);
  };

  const send = async () => {
    const txt = input.trim();
    if (!txt) return;
    setInput("");
    try {
      const r = await api.post("/chat/messages", { channel_id: activeChannel, body: txt });
      setMessages((m) => [...m, r.data]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
      setInput(txt);
    }
    // Riporta il focus all'input per scrivere subito il prossimo messaggio
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const MAX_BYTES = 200 * 1024;
  const ACCEPTED = "image/*,application/pdf";
  const onPickFile = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset così si può ri-selezionare lo stesso file
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File troppo grande (${Math.round(file.size / 1024)} KB) · massimo 200 KB`);
      return;
    }
    const mime = file.type || "";
    if (!(mime.startsWith("image/") || mime === "application/pdf")) {
      toast.error("Formato non consentito · solo immagini e PDF");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("channel_id", activeChannel);
      fd.append("file", file);
      const r = await api.post("/chat/messages/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((m) => [...m, r.data]);
      toast.success("Allegato inviato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      // Riporta il focus all'input testo (fix bug: dopo upload non si poteva più scrivere)
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const remove = async (mid) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await api.delete(`/chat/messages/${mid}`);
      setMessages((m) => m.filter((x) => x.id !== mid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  // ----- Gestione gruppi -----
  const toggleGroupMember = (uid) => {
    setGroupForm((f) => ({
      ...f,
      member_ids: f.member_ids.includes(uid) ? f.member_ids.filter((x) => x !== uid) : [...f.member_ids, uid],
    }));
  };
  const createGroup = async () => {
    const name = (groupForm.name || "").trim();
    if (!name) { toast.error("Nome obbligatorio"); return; }
    if (groupForm.member_ids.length === 0) { toast.error("Aggiungi almeno 1 membro"); return; }
    try {
      const r = await api.post("/chat/groups", { name, description: groupForm.description, member_ids: groupForm.member_ids });
      toast.success(`Gruppo "${name}" creato`);
      setGroupDialog(false);
      setGroupForm({ name: "", description: "", member_ids: [] });
      await loadContacts();
      setActiveChannel(r.data.id);
      setShowSidebar(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione gruppo");
    }
  };
  const openManageGroup = async (g) => {
    try {
      const r = await api.get(`/chat/groups/${g.id}`);
      setManageGroup(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const toggleManageMember = (uid) => {
    setManageGroup((g) => ({
      ...g,
      members: g.members.includes(uid) ? g.members.filter((x) => x !== uid) : [...g.members, uid],
    }));
  };
  const saveManageMembers = async () => {
    if (!manageGroup) return;
    try {
      await api.patch(`/chat/groups/${manageGroup.id}/members`, { member_ids: manageGroup.members });
      toast.success("Membri aggiornati");
      setManageGroup(null);
      loadContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Eliminare il gruppo "${g.name}"? Tutti i messaggi verranno nascosti.`)) return;
    try {
      await api.delete(`/chat/groups/${g.id}`);
      toast.success("Gruppo eliminato");
      setManageGroup(null);
      if (activeChannel === g.id) setActiveChannel("general");
      loadContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const leaveGroup = async (g) => {
    if (!window.confirm(`Uscire dal gruppo "${g.name}"?`)) return;
    try {
      await api.post(`/chat/groups/${g.id}/leave`);
      toast.success("Hai lasciato il gruppo");
      setManageGroup(null);
      if (activeChannel === g.id) setActiveChannel("general");
      loadContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const filteredContacts = contactSearch
    ? contacts.filter((c) => (c.full_name || c.email || "").toLowerCase().includes(contactSearch.toLowerCase()))
    : contacts;
  const filteredGroups = contactSearch
    ? groups.filter((g) => (g.name || "").toLowerCase().includes(contactSearch.toLowerCase()))
    : groups;

  const activeContact = contacts.find((c) => c.channel_id === activeChannel);
  const activeGroup = groups.find((g) => g.id === activeChannel);
  let activeTitle;
  if (activeChannel === "general") {
    activeTitle = { label: "Chat aziendale", subtitle: "Tutti i dipendenti", icon: Hash };
  } else if (activeGroup) {
    activeTitle = { label: activeGroup.name, subtitle: `Gruppo · ${activeGroup.members_count || 0} membri`, icon: Users };
  } else if (activeContact) {
    activeTitle = { label: activeContact.full_name || activeContact.email, subtitle: activeContact.email || "", icon: MessageCircle };
  } else {
    activeTitle = { label: "Chat", subtitle: "", icon: MessageCircle };
  }
  const TitleIcon = activeTitle.icon;

  const switchChannel = (cid) => {
    setActiveChannel(cid);
    setShowSidebar(false);
  };

  return (
    <div className={`bg-[#0A0E27] ${embedded ? "h-full" : "min-h-screen"} flex flex-col overflow-hidden`} data-testid="chat-module">
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Sidebar contatti */}
        <aside className={`w-full md:w-80 border-r border-[#1F2540] bg-white flex flex-col min-h-0 ${showSidebar ? "flex" : "hidden md:flex"}`} data-testid="chat-sidebar">
          <div className="p-4 border-b border-[#1F2540]">
            <h2 className="font-display text-2xl flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-[#3B82F6]"/> Chat
            </h2>
            <p className="text-xs text-[#757575] mt-1">Comunicazione interna aziendale</p>
            <div className="mt-3 relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-[#999]"/>
              <Input
                value={contactSearch}
                onChange={(e)=>setContactSearch(e.target.value)}
                placeholder="Cerca dipendente…"
                className="pl-9 rounded-full"
                data-testid="chat-contact-search"
              />
            </div>
          </div>

          <div className="overflow-auto flex-1 chat-scroll">
            {/* Generale */}
            <button
              onClick={()=>switchChannel("general")}
              data-testid="chat-channel-general"
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#1F2540] hover:bg-[#1E2347]/50 transition ${activeChannel === "general" ? "bg-[#1E2347]" : ""}`}
            >
              <div className="w-10 h-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center font-bold flex-shrink-0">
                <Hash className="w-5 h-5"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-2">
                  Generale
                  {unreadMap.general > 0 && (
                    <span className="bg-[#3B82F6] text-white text-[10px] font-bold px-1.5 py-0 rounded-full">{unreadMap.general}</span>
                  )}
                </div>
                <div className="text-xs text-[#757575] truncate">Canale aziendale · tutti i dipendenti</div>
              </div>
            </button>

            {/* Gruppi */}
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[#999] font-bold flex items-center justify-between">
              <span className="flex items-center gap-2"><Users className="w-3 h-3"/> Gruppi</span>
              <button
                onClick={() => { setGroupForm({ name: "", description: "", member_ids: [] }); setGroupDialog(true); }}
                data-testid="chat-create-group-btn"
                className="text-[#3B82F6] hover:bg-[#1E2347] rounded-full p-1"
                title="Crea nuovo gruppo"
              >
                <Plus className="w-3.5 h-3.5"/>
              </button>
            </div>
            {filteredGroups.length === 0 && !contactSearch && (
              <div className="px-4 pb-2 text-[11px] text-[#999] italic">Nessun gruppo · crea il primo</div>
            )}
            {filteredGroups.map((g) => {
              const isActive = g.id === activeChannel;
              const unread = unreadMap[g.id] || 0;
              return (
                <button
                  key={g.id}
                  onClick={() => switchChannel(g.id)}
                  data-testid={`chat-group-${g.id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#1F2540] hover:bg-[#1E2347]/50 transition ${isActive ? "bg-[#1E2347]" : ""}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#7C3AED] text-white flex items-center justify-center font-bold flex-shrink-0">
                    <Users className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm truncate">{g.name}</div>
                      {unread > 0 && (
                        <span className="bg-[#3B82F6] text-white text-[10px] font-bold px-1.5 py-0 rounded-full flex-shrink-0">{unread}</span>
                      )}
                    </div>
                    <div className="text-xs text-[#757575] truncate">{g.members_count} membri · {g.last_message || "Nessun messaggio"}</div>
                  </div>
                </button>
              );
            })}

            {/* Contatti DM */}
            <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-[#999] font-bold flex items-center gap-2">
              <MessageCircle className="w-3 h-3"/> Messaggi diretti
            </div>
            {filteredContacts.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#757575]">
                {contactSearch ? "Nessun risultato" : "Nessun contatto disponibile"}
              </div>
            ) : filteredContacts.map((c) => {
              const isActive = c.channel_id === activeChannel;
              const unread = unreadMap[c.channel_id] || 0;
              return (
                <button
                  key={c.id}
                  onClick={()=>switchChannel(c.channel_id)}
                  data-testid={`chat-contact-${c.id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#1F2540] hover:bg-[#1E2347]/50 transition ${isActive ? "bg-[#1E2347]" : ""}`}
                >
                  <Avatar avatar={c.avatar} name={c.full_name?.split(" ")[0]} lastname={c.full_name?.split(" ").slice(1).join(" ")} size={40}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm truncate">{c.full_name}</div>
                      {unread > 0 && (
                        <span className="bg-[#3B82F6] text-white text-[10px] font-bold px-1.5 py-0 rounded-full flex-shrink-0">{unread}</span>
                      )}
                    </div>
                    <div className="text-xs text-[#757575] truncate">
                      {c.last_message || c.email}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Thread */}
        <section className={`flex-1 flex flex-col min-h-0 ${showSidebar ? "hidden md:flex" : "flex"}`} data-testid="chat-thread">
          {/* Header */}
          <div className="p-3 md:p-4 border-b border-[#1F2540] bg-white flex items-center gap-3">
            <button onClick={()=>setShowSidebar(true)} className="md:hidden p-2 -ml-2 rounded-lg hover:bg-[#1E2347]" data-testid="chat-back-to-list">
              <ChevronLeft className="w-5 h-5"/>
            </button>
            {activeContact ? (
              <Avatar avatar={activeContact.avatar} name={activeContact.full_name?.split(" ")[0]} lastname={activeContact.full_name?.split(" ").slice(1).join(" ")} size={36}/>
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center"><TitleIcon className="w-5 h-5"/></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display text-base md:text-lg truncate" data-testid="chat-thread-title">{activeTitle.label}</div>
              <div className="text-xs text-[#757575] truncate">{activeTitle.subtitle}</div>
            </div>
            {activeGroup && (
              <button
                onClick={() => openManageGroup(activeGroup)}
                data-testid="chat-manage-group"
                className="p-2 rounded-full hover:bg-[#1E2347] text-[#3B82F6]"
                title="Gestisci gruppo"
              >
                <Pencil className="w-4 h-4"/>
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="relative flex-1 flex flex-col min-h-0">
            <div
              ref={scrollRef}
              onScroll={onScrollMessages}
              className="flex-1 overflow-y-auto chat-scroll p-4 md:p-6 space-y-3 bg-[#0A0E27]"
              data-testid="chat-messages"
            >
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center text-[#757575] text-sm">
                  <div>
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#3B82F6]/30"/>
                    <div>Nessun messaggio ancora.</div>
                    <div className="text-xs mt-1">Inizia la conversazione qui sotto.</div>
                  </div>
                </div>
              ) : (
                messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    msg={m}
                    mine={m.sender_id === user?.id}
                    onDelete={remove}
                    canDeleteAny={canDeleteAny}
                  />
                ))
              )}
            </div>
            {/* Pulsante "scorri in basso" con badge nuovi messaggi */}
            {!atBottom && (
              <button
                type="button"
                onClick={scrollToBottom}
                data-testid="chat-scroll-bottom-btn"
                aria-label={newMessagesCount > 0 ? `${newMessagesCount} nuovi messaggi · scorri in basso` : "Scorri in basso"}
                className="absolute right-4 bottom-4 z-20 rounded-full h-11 w-11 bg-[#3B82F6] hover:bg-[#7C3AED] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
              >
                <ChevronDown className="w-5 h-5"/>
                {newMessagesCount > 0 && (
                  <span
                    data-testid="chat-new-messages-badge"
                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0A0E27]"
                  >
                    {newMessagesCount > 99 ? "99+" : newMessagesCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e)=>{ e.preventDefault(); send(); }}
            className="p-3 md:p-4 border-t border-[#1F2540] bg-white flex items-center gap-2 sticky bottom-0 z-10"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            data-testid="chat-composer"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              onChange={onFileSelected}
              className="hidden"
              data-testid="chat-file-input"
            />
            <button
              type="button"
              onClick={onPickFile}
              disabled={uploading}
              data-testid="chat-attach-btn"
              title="Allega immagine o PDF (max 200 KB)"
              aria-label="Allega file"
              className="rounded-full h-10 w-10 p-0 flex items-center justify-center border border-[#1F2540] hover:border-[#3B82F6] hover:bg-[#1E2347] text-[#3B82F6] transition flex-shrink-0 disabled:opacity-50"
            >
              {uploading
                ? <div className="w-4 h-4 border-2 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"/>
                : <Paperclip className="w-4 h-4"/>}
            </button>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              placeholder={activeChannel === "general" ? "Messaggio a tutta l'azienda…" : "Scrivi un messaggio…"}
              className="rounded-full flex-1 min-w-0"
              data-testid="chat-input"
              maxLength={4000}
              enterKeyHint="send"
              autoComplete="off"
            />
            <Button
              type="submit"
              disabled={!input.trim()}
              data-testid="chat-send"
              className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] btn-glow h-10 w-10 p-0 flex-shrink-0"
              aria-label="Invia messaggio"
            >
              <Send className="w-4 h-4"/>
            </Button>
          </form>
        </section>
      </div>

      {/* Dialog crea gruppo */}
      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent className="rounded-2xl max-w-md max-h-[92vh] overflow-y-auto" data-testid="chat-create-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Users className="w-6 h-6 text-[#3B82F6]"/> Nuovo gruppo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome gruppo *</Label>
              <Input data-testid="chat-group-name" className="rounded-xl mt-1" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="Es. Cantiere Roma" maxLength={80}/>
            </div>
            <div>
              <Label>Descrizione (facoltativa)</Label>
              <Input data-testid="chat-group-description" className="rounded-xl mt-1" value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} placeholder="Es. Coordinamento squadra Roma" maxLength={240}/>
            </div>
            <div>
              <Label>Membri ({groupForm.member_ids.length} selezionati)</Label>
              <div className="mt-1 rounded-xl border border-[#1F2540] max-h-56 overflow-y-auto divide-y divide-[#1F2540]" data-testid="chat-group-members-list">
                {contacts.length === 0 && <div className="p-4 text-xs text-[#999] text-center">Nessun contatto disponibile</div>}
                {contacts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#1E2347]/30 cursor-pointer" data-testid={`chat-group-pick-${c.id}`}>
                    <Checkbox checked={groupForm.member_ids.includes(c.id)} onCheckedChange={() => toggleGroupMember(c.id)}/>
                    <Avatar avatar={c.avatar} name={c.full_name?.split(" ")[0]} lastname={c.full_name?.split(" ").slice(1).join(" ")} size={28}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{c.full_name}</div>
                      <div className="text-[11px] text-[#757575] truncate">{c.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setGroupDialog(false)} className="rounded-full">Annulla</Button>
            <Button onClick={createGroup} data-testid="chat-group-save" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">
              <Plus className="w-4 h-4 mr-1.5"/> Crea gruppo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog gestione gruppo */}
      <Dialog open={!!manageGroup} onOpenChange={(o) => { if (!o) setManageGroup(null); }}>
        <DialogContent className="rounded-2xl max-w-md max-h-[92vh] overflow-y-auto" data-testid="chat-manage-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Users className="w-6 h-6 text-[#3B82F6]"/> Gestisci gruppo</DialogTitle>
          </DialogHeader>
          {manageGroup && (() => {
            const isOwner = manageGroup.owner_id === user?.id;
            const canManage = isOwner || canDeleteAny;
            return (
              <div className="space-y-3">
                <div className="bg-[#1E2347]/50 border border-[#1F2540] rounded-xl p-3">
                  <div className="font-semibold">{manageGroup.name}</div>
                  {manageGroup.description && <div className="text-xs text-[#757575] mt-1">{manageGroup.description}</div>}
                  <div className="text-[11px] text-[#999] mt-1">{manageGroup.members?.length || 0} membri</div>
                </div>
                {canManage ? (
                  <div>
                    <Label>Membri</Label>
                    <div className="mt-1 rounded-xl border border-[#1F2540] max-h-56 overflow-y-auto divide-y divide-[#1F2540]" data-testid="chat-manage-members-list">
                      {contacts.map((c) => {
                        const isMember = manageGroup.members?.includes(c.id);
                        const isOwnerOfGroup = c.id === manageGroup.owner_id;
                        return (
                          <label key={c.id} className={`flex items-center gap-2 px-3 py-2 ${isOwnerOfGroup ? "opacity-50" : "hover:bg-[#1E2347]/30 cursor-pointer"}`}>
                            <Checkbox checked={isMember || isOwnerOfGroup} disabled={isOwnerOfGroup} onCheckedChange={() => toggleManageMember(c.id)}/>
                            <Avatar avatar={c.avatar} name={c.full_name?.split(" ")[0]} lastname={c.full_name?.split(" ").slice(1).join(" ")} size={28}/>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">{c.full_name} {isOwnerOfGroup && <span className="text-[10px] text-[#3B82F6]">(creatore)</span>}</div>
                              <div className="text-[11px] text-[#757575] truncate">{c.email}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-[#1F2540] p-3 space-y-2">
                    <div className="text-xs uppercase tracking-widest text-[#999] font-bold">Membri</div>
                    {(manageGroup.members_detail || []).map((m) => (
                      <div key={m.id} className="flex items-center gap-2 text-sm">
                        <Avatar avatar={m.avatar} name={m.full_name?.split(" ")[0]} lastname={m.full_name?.split(" ").slice(1).join(" ")} size={28}/>
                        <span>{m.full_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="ghost" onClick={() => setManageGroup(null)} className="rounded-full">Chiudi</Button>
            {manageGroup && manageGroup.owner_id !== user?.id && (
              <Button onClick={() => leaveGroup(manageGroup)} variant="outline" data-testid="chat-leave-group" className="rounded-full border-orange-400 text-orange-700">
                <LogOutIcon className="w-4 h-4 mr-1.5"/> Esci dal gruppo
              </Button>
            )}
            {manageGroup && (manageGroup.owner_id === user?.id || canDeleteAny) && (
              <>
                <Button onClick={() => deleteGroup(manageGroup)} variant="outline" data-testid="chat-delete-group" className="rounded-full border-red-400 text-red-700">
                  <Trash2 className="w-4 h-4 mr-1.5"/> Elimina
                </Button>
                <Button onClick={saveManageMembers} data-testid="chat-save-members" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">
                  Salva membri
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
